import React, { FC, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { BaseNames, getApiErrorMessage, isApiPayloadSuccess, KTSVG } from 'src/marketConnect/helpers'
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks'
import Platforms from '../../../listing/Platforms'
import {Spinner } from 'react-bootstrap'
import Loader from 'src/utils/Loader'
import { Platform, PlatformDataType } from 'src/interfaces/Platform.interface'
import useAppToast from 'src/hooks/useAppToast'
import { useNavigate } from 'react-router-dom'
import {
  connectPlatformAsync,
  deletePlatformDataAsync,
  fetchConnectedPlatformAsync,
  fetchPlatformsAsync,
  shopifyappUninstallAsync,
} from 'src/redux/actions/platformActions'
import ConnectPlatformModal from '../ConnectPlatformModal'
import PlatformCard from '../PlatformCard'
import clsx from 'clsx'
import { setStepsData } from 'src/redux/slices/tourPopupSlice'
import CommonAlertModal from 'src/utils/modals/CommonAlertModal'
import { fetchSkipSetupAsync, fetchUserAccountDetailsAsync } from 'src/redux/actions/accountActions'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAnglesRight } from '@fortawesome/free-solid-svg-icons'
import { useDebounce } from 'src/hooks/useDebounce'
import { Box, Divider, Tooltip, Typography, Checkbox, FormControlLabel } from '@mui/material'
import { filterTourPlatforms } from '../../utils/tourPlatformSearch'
import {
  isPrimaryPlatform,
  resolvePrimaryPlatform,
} from '../../utils/tourPlatformSearch'

const SOURCE_ALLOWED_NATURES = [
  'PRIMARY',
  'BOTH',
  'ACCOUNTING',
  'ACCOUNTING_SECONDARY',
  'SHIPPING',
  'SHIPPING_SECONDARY',
  'ERP',
  'ERP_SECONDARY',
] as const
type StepStatus = 'active' | 'inactive' | 'finished'

type TourSteps = {
  stepOne: StepStatus
  stepTwo: StepStatus
  stepThree: StepStatus
}

type TourStep1PropType = {
  confirmSkipOnboarding: boolean
  setConfirmSkipOnboarding:any
  // setStepsData: React.Dispatch<React.SetStateAction<TourSteps>>
  setSelectedPlatformId: React.Dispatch<React.SetStateAction<string | undefined>>
}

const TourStep1: FC<TourStep1PropType> = ({ setSelectedPlatformId, confirmSkipOnboarding, setConfirmSkipOnboarding }) => {
  const {fetchedConnectedPlatformData, fetchedConnectedPlatformLoading, mainStoreData} =
    useAppSelector((state) => state.fetchConnectedPlatform)
  const primaryConnectedPlatform = resolvePrimaryPlatform(
    fetchedConnectedPlatformData,
    mainStoreData
  )
  const hasPrimaryConnected = isPrimaryPlatform(primaryConnectedPlatform)

  const {fetchedUserAccountData} = useAppSelector((state) => state.userAccountData)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const {stepsData} = useAppSelector((state) => state.tourPopup)
  const [showConnectPlatformModal, setShowConnectPlatformModal] = useState<boolean>(false)
  const [selectedPlatform, setSelectedPlatform] = useState<Platform | null>(null)
  const [ecommercePlatform, setEcommercePlatform] = useState<any>([])
  const [showSettingModal, setShowSettingModal] = useState<boolean>(false)
  const [isSkipping, setIsSkipping] = useState<boolean>(false)
  const [modalData, setModalData] = useState<PlatformDataType>()
  const [confirmChannelDelete, setConfirmChannelDelete] = useState<boolean>(false)
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const [deleting, setDeleting] = useState(false)
  const {successToast, warningToast, errorToast} = useAppToast()
  const [connectingPlatformId, setConnectingPlatformId] = useState<{
    platformId: number | string | null
    isConnecting: boolean
  }>({isConnecting: false, platformId: null})

  const allowedSources = ['COMMERCIUM','WOOCOMMERCE','VEEQO','QBO','PRESTASHOP','DISCONNECT','BIGCOMMERCE','GOOGLE','TIKTOK'];
  const debouncedSearch = useDebounce(search, 500);

  const [isConnectedPlatformModal, setIsConnectedPlatformModal] = useState(false);

  useEffect(() => {
    if (hasPrimaryConnected) {
      setIsConnectedPlatformModal(false)
      setLoading(false)
      return
    }

    if (search !== debouncedSearch) {
      return
    }

    let cancelled = false
    setLoading(true)

    const trimmedSearch = debouncedSearch.trim()
    const query = trimmedSearch.length > 0 ? {search: trimmedSearch} : undefined

    dispatch(fetchPlatformsAsync(query))
      .unwrap()
      .then((res) => {
        if (cancelled) {
          return
        }
        setEcommercePlatform(
          filterTourPlatforms(res?.data, SOURCE_ALLOWED_NATURES, debouncedSearch)
        )
        setIsConnectedPlatformModal(true)
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) {
          return
        }
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [debouncedSearch, search, dispatch, hasPrimaryConnected]);

  return (
    <>
      {primaryConnectedPlatform ? (
        <div className='alert alert-dismissible bg-light-success d-flex flex-column flex-sm-row p-3 d-flex justify-content-between'>
          <div className='d-flex'>
            <span className='svg-icon svg-icon-2hx svg-icon-light me-4 mb-5 mb-sm-0'>
              <img
                src={primaryConnectedPlatform?.platform_info?.logo_ref!}
                className='h-40px'
              />
            </span>
            <div className='d-flex flex-column text-light pe-0 pe-sm-10'>
              <h5 className='mb-1 text-success'>
                {primaryConnectedPlatform?.display_name ?? ''}
              </h5>
              <span className='text-success'>
                {hasPrimaryConnected
                  ? `${BaseNames.PRIMARY_Name} Channel Connected`
                  : `${BaseNames.SECONDARY_Name} Channel Connected`}
              </span>
            </div>
          </div>
          <div className='d-flex justify-content-center align-items-center'>
            {allowedSources.includes(fetchedUserAccountData?.source!) &&
            (['DISCONNECT', 'GOOGLE'].includes(fetchedUserAccountData?.source!)
              ? fetchedUserAccountData?.is_subscribed
              : true) ? (
              <button
                className='btn btn-sm btn-outline border-0 fw-bold text-white py-2'
                title='Disconnect the channel'
                onClick={() => setConfirmChannelDelete(true)}
              >
                <i className='bi bi-trash-fill fs-3 text-danger'></i>
              </button>
            ) : null}
            <span className='svg-icon svg-icon-2x svg-icon-light ms-2' title=''>
              <KTSVG
                path='media/icons/duotune/general/gen043.svg'
                className='svg-icon-success svg-icon-2hx'
              />
            </span>
          </div>
        </div>
      ) : null}
      {!hasPrimaryConnected ? (
        <button
          style={{position: 'relative', left: '75.5rem'}}
          className='btn btn-sm btn-primary fs-6 fw-bold'
          onClick={() => setConfirmSkipOnboarding(true)}
        >
          Skip Setup
          <span className='ms-2'>
            <FontAwesomeIcon icon={faAnglesRight} />
          </span>
        </button>
      ) : null}
      {!hasPrimaryConnected ? (
      <>
      <div>
        <div className='p-2 px-5 rounded bg-light-info my-2 fs-5 mb-4 text-center'>
          <i className='bi bi-telephone-fill fw-bold text-primary fs-5'></i> Facing difficulties in
          setup ? book a free assisted demo & onboarding call by Clicking{' '}
          <a
            className='fw-bold'
            target='_blank'
            href='https://calendly.com/d/crrg-dfq-xkv/product-demo-integration-flow-discussion'
            rel='noreferrer'
          >
            Here!
          </a>
        </div>
        <div className='d-flex align-items-center justify-content-between'>
          <h4>
            {connectingPlatformId.isConnecting ? (
              ''
            ) : (
              <>
                <h5 className='mb-0 fw-semibold text-dark'>
                  Connect {BaseNames.PRIMARY_Name} Channel
                </h5>
                <Tooltip
                  placement='bottom-start'
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: '#333',
                        color: '#fff',
                        fontSize: 13,
                        p: .5,
                        borderRadius: 1.5,
                        boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
                        maxWidth: 'min(640px, 95vw)',
                        position: 'relative',
                        left: {xs: 0, sm: 0},
                      },
                    },
                    arrow: {
                      sx: {
                        color: '#333',
                      },
                    },
                    popper: {
                      modifiers: [
                        {
                          name: 'offset',
                          options: {
                            offset: [0, 8],
                          },
                        },
                      ],
                    },
                  }}
                  title={
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        width: '100%',
                      }}
                    >
                      <Box sx={{p: .5}}>
                        <Typography fontWeight={700}>
                          * If you add this store as a Source platform then you will be able to send below data from this store to other platforms that you connect here:
                        </Typography>
                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                          {[
                            'Products',
                            'Inventories',
                            'Price Updates',
                            'Shipments',
                          ].map((item) => (
                            <FormControlLabel
                              key={item}
                              control={
                                <Checkbox
                                  size='small'
                                  checked
                                  disabled
                                  sx={{
                                    p: 0.3,
                                    '&.Mui-disabled': {color: '#22c55e'},
                                    '&.Mui-checked.Mui-disabled': {color: '#22c55e'},
                                  }}
                                />
                              }
                              label={<Typography variant='body2'>{item}</Typography>}
                              sx={{m: 0}}
                            />
                          ))}
                        </Box>
                      </Box>
                      <Box sx={{p: .5}}>
                        <Typography fontWeight={700}>
                          & you will be able to receive below data from other platforms to this Source platform that you connect here:
                        </Typography>
                        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                          {[
                            'Orders',
                            'Customers',
                            'Payments',
                            'Invoices',
                            'Returns/Refunds',
                            'Cancellations',
                            'Marketplace Fund Settlements',
                          ].map((item) => (
                            <FormControlLabel
                              key={item}
                              control={
                                <Checkbox
                                  size='small'
                                  checked
                                  disabled
                                  sx={{
                                    p: 0.3,
                                    '&.Mui-disabled': {color: '#22c55e'},
                                    '&.Mui-checked.Mui-disabled': {color: '#22c55e'},
                                  }}
                                />
                              }
                              label={<Typography variant='body2'>{item}</Typography>}
                              sx={{m: 0}}
                            />
                          ))}
                        </Box>
                      </Box>
                      <Divider sx={{borderColor: '#f8f8f8ff', borderBottomWidth: 1, width: '100%'}} />
                      <Typography component='span' sx={{color: '#fff', fontSize: 12}}>
                        Note : This is recommended option if you are looking to connect your store with a marketplace. If still not sure reach us out at{' '}
                        <span style={{color: '#3e97ff'}}>help@mycommercium.com</span>
                      </Typography>
                    </Box>
                  }
                >
                  <Box
                    className='d-flex align-items-center gap-2 mt-2 ms-1'
                    sx={{cursor: 'pointer'}}
                  >
                    <Typography variant='body2' color='text.secondary' sx={{mt: 0.3}}>
                      What is {BaseNames.PRIMARY_Name} Channel?
                    </Typography>
                    <i className='bi bi-info-circle fs-5 text-secondary' />
                  </Box>
                </Tooltip>
              </>
            )}
          </h4>
          {isConnectedPlatformModal &&
          // Show search even if source isn’t in the original allowed list; only gate for unsubscribed DISCONNECT/GOOGLE users
          (['DISCONNECT', 'GOOGLE'].includes(fetchedUserAccountData?.source!)
            ? fetchedUserAccountData?.is_subscribed
            : true) ? (
            <div style={{display: 'flex', gap: 10}}>
              <div className='position-relative w-350px'>
                <span className='search-icon'>
                  <i className='fas fa-search' />
                </span>
                <input
                  className='form-control ps-5 py-2 text-gray  input-sm'
                  type='search'
                  placeholder='Search Channels'
                  onChange={(e) => setSearch(e.target.value)}
                  value={search}
                />
              </div>
              {/* <button type='button' className='btn btn-sm btn-primary fs-6 fw-bold' 
              onClick={()=>{
                  dispatch(setStepsData({stepData:stepsData,visible:false}))
                  navigate('/pricing')
                }} > <i className={'bi bi-coin fs-4 me-2'}></i> Manage Plan</button> */}
            </div>
          ) : null}
        </div>
      </div>
      <div className='py-4'>
        <div
          className={clsx(
            'row g-3 gx-2 g-xl-8 ',
            connectingPlatformId.isConnecting ? 'justify-content-center' : ''
          )}
        >
          {/* loader added */}
          {loading ? (
            <div className='text-center py-10'>
              <Loader variant="section" />
            </div>
          ) : ecommercePlatform?.length > 0 ? (
            ecommercePlatform.map((item: Platform) => {
              if (connectingPlatformId.isConnecting) {
                if (connectingPlatformId.platformId === item.id) {
                  return (
                    <PlatformCard
                      key={item.id}
                      data={item}
                      onClickConnectTop={setConnectingPlatformId}
                      // setStepsData={setStepsData}
                      setSelectedPlatformId={setSelectedPlatformId}
                      isPrimary={true}
                      setIsConnectedPlatformModal={setIsConnectedPlatformModal}
                    />
                  )
                }
                return null // Prevents undefined in map
              } else {
                return (
                  <PlatformCard
                    key={item.id}
                    data={item}
                    onClickConnectTop={setConnectingPlatformId}
                    isPrimary={true}
                    // setStepsData={setStepsData}
                    setSelectedPlatformId={setSelectedPlatformId}
                    setIsConnectedPlatformModal={setIsConnectedPlatformModal}
                  />
                )
              }
            })
          ) : (
            <div className='p-4 bg-light-primary'>
              <h5>We couldn't find any platform matching your search</h5>
            </div>
          )}
        </div>
      </div>
      </>
      ) : null}
      {/* { showConnectPlatformModal ? 
        <ConnectPlatformModal
        isVisible={showConnectPlatformModal}
        closeModal={handleCloseModal}
        data={selectedPlatform}
        onDataReceived={handleDataReceived} // Pass the data receiving function
      /> : null } */}
      <CommonAlertModal
        key={'Delete Channel Confirmation'}
        title={'Are you sure, you want to disconnect the connected channel?'}
        modalClassName='custmd'
        subtitle=''
        successDisabled={deleting}
        isTermsVisible={false}
        successBtnTitle={deleting ? 'Disconnecting...' : 'Yes Disconnect'}
        isVisible={confirmChannelDelete}
        onCancel={() => {
          setConfirmChannelDelete(false)
        }}
        onSuccess={async () => {
          const platformId = primaryConnectedPlatform?.id
          if (!platformId) {
            warningToast('Platform id is undefined.')
            return
          }

          setDeleting(true)
          try {
            const response = await dispatch(deletePlatformDataAsync({id: platformId}))
            if (isApiPayloadSuccess(response)) {
              await dispatch(fetchConnectedPlatformAsync())
              successToast('Channel disconnected successfully!')
              dispatch(
                setStepsData({
                  stepData: {stepOne: 'active', stepTwo: 'inactive', stepThree: 'inactive'},
                  visible: true,
                })
              )
            } else {
              warningToast(getApiErrorMessage(response, 'Channel not disconnected'))
            }
          } finally {
            setDeleting(false)
            setConfirmChannelDelete(false)
          }
        }}
      />

      <CommonAlertModal
        key={'Skip Onboarding Confirmation'}
        title={
          'This Onboarding process guide you to do setup by showing steps, Are you sure you want to skip this & explore on your own?'
        }
        subtitle=''
        isTermsVisible={false}
        successDisabled={isSkipping}
        successBtnTitle={isSkipping ? 'Updating...' : 'Yes, Skip it.'}
        cancelBtnTitle={'Cancel'}
        showCloseButton={true}
        modalClassName='custmd'
        isVisible={confirmSkipOnboarding}
        onCancel={() => {
          setConfirmSkipOnboarding(false)
        }}
        onSuccess={async () => {
          setIsSkipping(true)
          const response = await dispatch(fetchSkipSetupAsync())
          if (response?.payload?.status_code === 200) {
            dispatch(fetchUserAccountDetailsAsync())
            navigate('/dashboard')
          }
        }}
      />
    </>
  )
}

export default TourStep1
